import React, { useState, useEffect } from 'react';
import { FaSignOutAlt, FaBars, FaTimes, FaUserCog, FaEdit, FaTrash, FaPlus, FaUpload, FaFileAlt, FaUser } from 'react-icons/fa';
import './Dashboard.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const USERS_PER_PAGE = 10;
// Enable below for debugging in local development
//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');
const API_BASE_URL = 'https://eliteapilatest.onrender.com/api';

const Dashboard = ({ hideManageUsers = false, viewerOnly = false, allowedClasses = [], title = 'Admin Dashboard', schoolName: initialSchoolName = 'default' }) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [activeTab, setActiveTab] = useState('microSchedule');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [schoolName, setSchoolName] = useState(initialSchoolName);

  const normalizeClassValue = (value) => {
    if (!value) return null;
    const mapping = {
      'I': '1', 'II': '2', 'III': '3', 'IV': '4', 'V': '5',
      'VI': '6', 'VII': '7', 'VIII': '8', 'IX': '9', 'X': '10',
      'Class I': '1', 'Class II': '2', 'Class III': '3', 'Class IV': '4', 'Class V': '5',
      'Class VI': '6', 'Class VII': '7', 'Class VIII': '8', 'Class IX': '9', 'Class X': '10'
    };
    const trimmed = String(value).trim();
    return mapping[trimmed] || trimmed;
  };

  const sanitizeFolderName = (value) => {
    if (!value) return 'default';
    return String(value).trim().replace(/[\\/]+/g, '_').replace(/[^a-zA-Z0-9 _-]/g, '_');
  };

  const normalizedAllowedClasses = allowedClasses.map(normalizeClassValue).filter(Boolean);
  const allowedClassSet = new Set(normalizedAllowedClasses);

  // User management state
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userForm, setUserForm] = useState({ id: null, name: "", email: "", password: "", role: "Staff", classes: {} });
  const [formErrors, setFormErrors] = useState({});

  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [loggedInUserName, setLoggedInUserName] = useState('');

  // Fetch users from API
  useEffect(() => {
    // always fetch users for results grid and management
    fetchUsers();
    // also pull logged in user name
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setLoggedInUserName(parsed.name || '');
      } catch (e) {}
    }
    // eslint-disable-next-line
  }, []);

  // Fetch uploaded files when class is selected
  useEffect(() => {
    if (selectedClass && selectedProgram) {
      fetchUploadedFiles();
    }
    // eslint-disable-next-line
  }, [selectedClass, selectedProgram]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users`);
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      alert('Failed to fetch users');
    }
  };

  // Helper function to extract class number from class name
  const getClassNumber = (className) => {
    const classMap = {
      'Class I': '1', 'Class II': '2', 'Class III': '3', 'Class IV': '4', 'Class V': '5',
      'Class VI': '6', 'Class VII': '7', 'Class VIII': '8', 'Class IX': '9', 'Class X': '10'
    };
    return classMap[className] || '1';
  };

  // Helper function to get directory for each tab
  const getTabDirectory = (tab, classNum) => {
    const dirMap = {
      'microSchedule': `MicroSchedule/${classNum}`,
      'examinations': `Examinations/${classNum}`,
      'analysis': `Analysis/${classNum}`,
      'keySolutions': `Key/${classNum}`,
      'results': `Results/${sanitizeFolderName(schoolName)}`
    };
    return dirMap[tab] || '';
  };

  const classOptions = ['1','2','3','4','5','6','7','8','9','10'];

  const classesToText = (classes = {}) => {
    return classOptions.filter(c => classes[c]).join(', ') || '-';
  };

  const toggleClassSelection = (userState, classNum) => {
    const updatedClasses = { ...(userState.classes || {}) };
    updatedClasses[classNum] = !updatedClasses[classNum];
    return { ...userState, classes: updatedClasses };
  };

  const validateUserForm = () => {
    const errors = {};
    if (!userForm.name.trim()) errors.name = 'Name is required';
    if (!userForm.email.trim()) errors.email = 'Email is required';
    if (!userForm.password.trim()) errors.password = 'Password is required';
    if (!userForm.role) errors.role = 'Role is required';
    if (userForm.role !== 'Admin') {
      const selectedClasses = classOptions.filter(c => userForm.classes?.[c]);
      if (selectedClasses.length === 0) errors.classes = 'Select at least one class';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddUserModal = () => {
    setUserForm({ id: null, name: "", email: "", password: "", role: "Staff", classes: {} });
    setFormErrors({});
    setIsEditMode(false);
    setShowUserModal(true);
  };

  const openEditUserModal = (user) => {
    setUserForm({ id: user.id, name: user.name || "", email: user.email || "", password: user.password || "", role: user.role || "Staff", classes: user.classes || {} });
    setFormErrors({});
    setIsEditMode(true);
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setFormErrors({});
  };

  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUserClassToggle = (classNum) => {
    setUserForm(prev => toggleClassSelection(prev, classNum));
  };

  const handleSaveUser = async () => {
    if (!validateUserForm()) return;
    try {
      const url = isEditMode ? `${API_BASE_URL}/users/${userForm.id}` : `${API_BASE_URL}/users`;
      const method = isEditMode ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm),
      });
      if (response.ok) {
        fetchUsers();
        closeUserModal();
      } else {
        alert('Failed to save user');
      }
    } catch (error) {
      alert('Failed to save user');
    }
  };

  const handleManageUsersClick = () => {
    setShowManageUsers(true);
    setSelectedProgram(null);
    setSelectedClass(null);
  };

  const handleAddUser = openAddUserModal;

  const handleEditUser = (user) => {
    openEditUserModal(user);
  };

  const handleUpdateUser = handleSaveUser;

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchUsers();
      } else {
        alert('Failed to delete user');
      }
    } catch {
      alert('Failed to delete user');
    }
  };

  // Pagination
  const getTabLabel = (tab) => {
    const labels = {
      'microSchedule': '📅 Micro Schedule',
      'examinations': '📝 Examinations',
      'analysis': '📊 Analysis',
      'keySolutions': '🔍 Key & Solutions',
      'results': '🏆 Results'
    };
    return labels[tab] || '';
  };

  // Map internal tab key to API type name
  const tabToTypeName = (tab) => {
    if (tab === 'microSchedule') return 'MicroSchedule';
    if (tab === 'examinations') return 'Examinations';
    if (tab === 'analysis') return 'Analysis';
    if (tab === 'keySolutions') return 'Key';
    if (tab === 'results') return 'Results';
    return 'MicroSchedule';
  };

  const getTabFolderIdentifier = (tab, owner = null) => {
    if (tab === 'results') {
      return sanitizeFolderName(owner || loggedInUserName || schoolName || 'default');
    }
    return getClassNumber(selectedClass);
  };

  // Fetch uploaded files for the selected class
  const fetchUploadedFiles = async () => {
    try {
      if (!selectedClass) return;
      const tabs = ['microSchedule', 'examinations', 'analysis', 'keySolutions', 'results'];
      const files = {};

      for (const tab of tabs) {
        if (tab === 'results') {
          try {
            if (viewerOnly) {
              const safeUser = sanitizeFolderName(loggedInUserName || schoolName || 'default');
              const resp = await fetch(`${API_BASE_URL}/files/results/${encodeURIComponent(safeUser)}`);
              if (!resp.ok) {
                files.results = {};
                continue;
              }
              const json = await resp.json();
              files.results = {};
              if (Array.isArray(json.files) && json.files.length > 0) {
                files.results[safeUser] = json.files[0];
              }
            } else {
              const resp = await fetch(`${API_BASE_URL}/files/results/all`);
              if (!resp.ok) {
                files.results = {};
                continue;
              }
              const json = await resp.json();
              files.results = json.files || {};
            }
          } catch (err) {
            files.results = {};
            continue;
          }
        } else {
          const typeName = tabToTypeName(tab);
          const identifier = getTabFolderIdentifier(tab);
          try {
            const resp = await fetch(`${API_BASE_URL}/files/${encodeURIComponent(typeName)}/${encodeURIComponent(identifier)}`);
            if (!resp.ok) {
              continue;
            }
            const json = await resp.json();
            if (Array.isArray(json.files) && json.files.length > 0) {
              files[tab] = json.files[0];
            }
          } catch (err) {
            continue;
          }
        }
      }

      setUploadedFiles(files);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event, tab, owner = null) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      let endpoint = `${API_BASE_URL}/upload`;
      if (tab === 'results' && owner) {
        const safeOwner = sanitizeFolderName(owner);
        formData.append('type', 'Results');
        formData.append('classNum', safeOwner);
      } else {
        const identifier = getTabFolderIdentifier(tab);
        formData.append('type', tabToTypeName(tab));
        formData.append('classNum', identifier);
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (tab === 'results') {
          const key = sanitizeFolderName(owner || 'unknown');
          setUploadedFiles({
            ...uploadedFiles,
            results: {
              ...(uploadedFiles.results || {}),
              [key]: data.filename || file.name
            }
          });
        } else {
          setUploadedFiles({
            ...uploadedFiles,
            [tab]: data.filename || file.name
          });
        }
        alert('File uploaded successfully!');
      } else {
        alert('Failed to upload file. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading file');
    } finally {
      setUploading(false);
      try { event.target.value = ''; } catch (e) {}
    }
  };

  // Handle file deletion
  const handleDeleteFile = async (tab, owner = null) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      let fileName = null;
      let endpoint = '';
      if (tab === 'results') {
        const key = sanitizeFolderName(owner || 'unknown');
        fileName = (uploadedFiles.results || {})[key];
        endpoint = `${API_BASE_URL}/files/results/${encodeURIComponent(key)}/${encodeURIComponent(fileName)}`;
      } else {
        fileName = uploadedFiles[tab];
        const identifier = getTabFolderIdentifier(tab);
        const typeName = tabToTypeName(tab);
        endpoint = `${API_BASE_URL}/files/${encodeURIComponent(typeName)}/${encodeURIComponent(identifier)}/${encodeURIComponent(fileName)}`;
      }

      if (!fileName) {
        alert('No file found to delete');
        return;
      }

      const response = await fetch(endpoint, { method: 'DELETE' });

      if (response.ok) {
        const newFiles = { ...uploadedFiles };
        if (tab === 'results') {
          const key = sanitizeFolderName(owner || 'unknown');
          if (newFiles.results) delete newFiles.results[key];
        } else {
          delete newFiles[tab];
        }
        setUploadedFiles(newFiles);
        alert('File deleted successfully!');
      } else {
        alert('Failed to delete file. Please try again.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting file');
    }
  };

  const programs = {
    'Prime Programme': ['Class I', 'Class II', 'Class III', 'Class IV', 'Class V'],
    'Techno Unique': ['Class VI', 'Class VII', 'Class VIII', 'Class IX', 'Class X'],
    'Olympiad Spectra': ['Class VI', 'Class VII', 'Class VIII', 'Class IX']
  };

  const programList = Object.keys(programs).filter((program) => {
    if (!viewerOnly || normalizedAllowedClasses.length === 0) return true;
    return programs[program].some(cls => allowedClassSet.has(getClassNumber(cls)));
  });

  const handleProgramClick = (program) => {
    setSelectedProgram(program === selectedProgram ? null : program);
    setSelectedClass(null);
    setShowManageUsers(false);
  };

  const handleClassClick = (cls) => {
    setSelectedClass(cls);
    setActiveTab('microSchedule'); // Set default tab to Micro Schedule
    setShowManageUsers(false);
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setSchoolName(parsed.schoolName || parsed.name || 'default');
    }
  }, []);

  const handleLogout = () => {
    window.location.href = '/';
  };

  // Pagination
  const totalPages = Math.ceil(users.length / USERS_PER_PAGE);
  const paginatedUsers = users.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <header className="header-container">
        <div className="logo">{title}</div>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <button className="logout-button-header" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </header>

      {/* Main Body Container */}
      <div className="body-container">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="sidebar-user">
            <FaUser />
            <div>
              <div className="sidebar-user-welcome">Welcome</div>
              <strong>{loggedInUserName || 'User'}</strong>
            </div>
          </div>
          {!hideManageUsers && (
            <button
              className={`program-button ${showManageUsers ? 'active' : ''}`}
              onClick={handleManageUsersClick}
            >
              <FaUserCog style={{ marginRight: 8 }} />
              Manage Users
            </button>
          )}
          {programList.length === 0 && (
            <div className="sidebar-empty">
              No classes available for this school account.
            </div>
          )}
          {programList.map((program) => (
            <div key={program}>
              <button
                className="program-button"
                onClick={() => handleProgramClick(program)}
              >
                {program} {selectedProgram === program ? '▼' : '▶'}
              </button>
              {selectedProgram === program && (
                <div className="class-list">
                  {programs[program]
                    .filter(cls => !viewerOnly || allowedClassSet.has(getClassNumber(cls)))
                    .map((cls) => (
                      <button
                        key={cls}
                        className="class-button"
                        onClick={() => handleClassClick(cls)}
                      >
                        {cls}
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="main-content">
          {showManageUsers ? (
            <div className="user-management">
              <div className="user-header-row">
                <h2>
                  <FaUserCog style={{ marginRight: 8 }} />
                  Manage Users
                </h2>
                <button className="add-user-toggle" onClick={openAddUserModal}>
                  <FaPlus /> Add User
                </button>
              </div>

              {showUserModal && (
                <div className="modal-overlay">
                  <div className="modal-popup">
                    <div className="modal-header">
                      <h3>{isEditMode ? 'Edit User' : 'Add User'}</h3>
                      <button className="modal-close" onClick={closeUserModal}>×</button>
                    </div>
                    <div className="modal-body">
                      <div className="modal-field-row">
                        <label>Name</label>
                        <input
                          type="text"
                          value={userForm.name}
                          onChange={e => handleUserFormChange('name', e.target.value)}
                        />
                        {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                      </div>
                      <div className="modal-field-row">
                        <label>Email</label>
                        <input
                          type="email"
                          value={userForm.email}
                          onChange={e => handleUserFormChange('email', e.target.value)}
                        />
                        {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                      </div>
                      <div className="modal-field-row">
                        <label>Password</label>
                        <input
                          type="password"
                          value={userForm.password}
                          onChange={e => handleUserFormChange('password', e.target.value)}
                        />
                        {formErrors.password && <span className="error-text">{formErrors.password}</span>}
                      </div>
                      <div className="modal-field-row">
                        <label>Role</label>
                        <select
                          value={userForm.role}
                          onChange={e => handleUserFormChange('role', e.target.value)}
                        >
                          <option value="Staff">Staff</option>
                          <option value="Admin">Admin</option>
                          <option value="Student">Student</option>
                        </select>
                        {formErrors.role && <span className="error-text">{formErrors.role}</span>}
                      </div>
                      {userForm.role !== 'Admin' && (
                        <>
                          <div className="modal-label">Classes</div>
                          <div className="class-selection-grid">
                            {classOptions.map(num => (
                              <label key={num} className="class-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={!!userForm.classes[num]}
                                  onChange={() => handleUserClassToggle(num)}
                                />
                                Class {num}
                              </label>
                            ))}
                          </div>
                          {formErrors.classes && <span className="error-text">{formErrors.classes}</span>}
                        </>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button className="cancel-btn" onClick={closeUserModal}>Cancel</button>
                      <button className="add-user-btn" onClick={handleSaveUser}>
                        <FaPlus /> {isEditMode ? 'Update User' : 'Save User'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* User Table */}
              <div className="user-table-container">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Role</th>
                      <th>Classes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user, index) => (
                      <tr key={`${user.id}-${sanitizeFolderName(user.name)}-${index}`}>
                        <td>{user.id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>******</td>
                        <td>{user.role}</td>
                        <td>{classesToText(user.classes)}</td>
                        <td>
                          <button className="edit-btn" onClick={() => openEditUserModal(user)}>
                            <FaEdit />
                          </button>
                          <button className="delete-btn" onClick={() => handleDeleteUser(user.id)}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : selectedClass ? (
            <div className="class-content-container">
              <h2 className="header-title">{selectedProgram} ➝ {selectedClass}</h2>
              
              {/* Tab Navigation */}
              <div className="tabs-container">
                <div className="tabs">
                  {['microSchedule', 'examinations', 'analysis', 'keySolutions', 'results'].map((tab) => (
                    <button
                      key={tab}
                      className={`tab-button ${tab} ${activeTab === tab ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {getTabLabel(tab)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === 'results' ? (
                  viewerOnly ? (
                    <div className="viewer-section">
                      <h3>{getTabLabel(activeTab)}</h3>
                      {uploadedFiles.results && uploadedFiles.results[sanitizeFolderName(loggedInUserName)] ? (
                        <div className="download-card">
                          <div className="download-info">
                            <FaFileAlt className="file-icon" />
                            <div>
                              <p className="download-label">Ready to download</p>
                              <p className="file-name">{uploadedFiles.results[sanitizeFolderName(loggedInUserName)]}</p>
                              <p className="download-subtext">From Results/{sanitizeFolderName(loggedInUserName)}</p>
                            </div>
                          </div>
                          <a
                            className="download-file-btn"
                            href={`${API_BASE_URL}/download/${encodeURIComponent(tabToTypeName(activeTab))}/${encodeURIComponent(getTabFolderIdentifier(activeTab, loggedInUserName))}/${encodeURIComponent(uploadedFiles.results[sanitizeFolderName(loggedInUserName)])}`}
                            download={uploadedFiles.results[sanitizeFolderName(loggedInUserName)]}
                          >
                            Download {getTabLabel(activeTab)}
                          </a>
                        </div>
                      ) : (
                        <div className="no-file viewer-no-file">
                          <p>No published {getTabLabel(activeTab)} file available yet for you.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="upload-section">
                      <h3>{getTabLabel(activeTab)}</h3>
                      <div className="results-grid">
                        <table className="user-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Upload / File</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((u, index) => {
                              const fileKey = sanitizeFolderName(u.name || `user_${u.id}`);
                              const fileForUser = (uploadedFiles.results || {})[fileKey];
                              return (
                                <tr key={`${u.id}-${fileKey}-${index}`}>
                                  <td>{u.name}</td>
                                  <td>
                                    {fileForUser ? (
                                      <div className="file-preview">
                                        <div className="file-info">
                                          <FaFileAlt className="file-icon" />
                                          <span className="file-name">{fileForUser}</span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="no-file">No file</div>
                                    )}
                                  </td>
                                  <td>
                                    <label className="upload-label" style={{marginRight:8}}>
                                      Upload
                                      <input
                                        type="file"
                                        accept=".pdf,.xlsx,.xls"
                                        onChange={(e) => handleFileUpload(e, 'results', u.name)}
                                        style={{display:'none'}}
                                      />
                                    </label>
                                    {fileForUser && (
                                      <button className="delete-file-btn" onClick={() => handleDeleteFile('results', u.name)}>
                                        <FaTrash /> Remove
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ) : (
                  viewerOnly ? (
                    <div className="viewer-section">
                      <h3>{getTabLabel(activeTab)}</h3>
                      {uploadedFiles[activeTab] ? (
                        <div className="download-card">
                          <div className="download-info">
                            <FaFileAlt className="file-icon" />
                            <div>
                              <p className="download-label">Ready to download</p>
                              <p className="file-name">{uploadedFiles[activeTab]}</p>
                              <p className="download-subtext">From {tabToTypeName(activeTab)}/{getTabFolderIdentifier(activeTab)}</p>
                            </div>
                          </div>
                          <a
                            className="download-file-btn"
                            href={`${API_BASE_URL}/download/${encodeURIComponent(tabToTypeName(activeTab))}/${encodeURIComponent(getTabFolderIdentifier(activeTab))}/${encodeURIComponent(uploadedFiles[activeTab])}`}
                            download={uploadedFiles[activeTab]}
                          >
                            Download {getTabLabel(activeTab)}
                          </a>
                        </div>
                      ) : (
                        <div className="no-file viewer-no-file">
                          <p>No published {getTabLabel(activeTab)} file available yet.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="upload-section">
                      <h3>{getTabLabel(activeTab)}</h3>
                      {uploadedFiles[activeTab] ? (
                        <div className="file-preview">
                          <div className="file-info">
                            <FaFileAlt className="file-icon" />
                            <span className="file-name">{uploadedFiles[activeTab]}</span>
                          </div>
                          <button
                            className="delete-file-btn"
                            onClick={() => handleDeleteFile(activeTab)}
                            title="Delete this file"
                          >
                            <FaTrash /> Remove
                          </button>
                        </div>
                      ) : (
                        <div className="no-file">
                          <p>No document uploaded yet</p>
                        </div>
                      )}

                      {!uploadedFiles[activeTab] && (
                        <div className="upload-input-container">
                          <label htmlFor={`file-input-${activeTab}`} className="upload-label">
                            <FaUpload /> Upload Document
                          </label>
                          <input
                            id={`file-input-${activeTab}`}
                            type="file"
                            accept=".pdf,.doc,.docx,.xlsx,.xls,.pptx,.ppt"
                            onChange={(e) => handleFileUpload(e, activeTab)}
                            disabled={uploading}
                            className="file-input"
                          />
                          {uploading && <span className="uploading-text">Uploading...</span>}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          ) : (
            <div className="welcome-message">
              <h2>Welcome to the {title}</h2>
              <p>Select a program and class from the sidebar to download published documents.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;